import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../../App";
import { 
  Users, Search, Shield, Heart, User, Mail 
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogHeader } from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [weddings, setWeddings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleData, setRoleData] = useState({ role: "", wedding_id: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };
      const [usersRes, weddingsRes] = await Promise.all([
        axios.get(`${API}/users`, { headers }),
        axios.get(`${API}/weddings`, { headers }),
      ]);
      setUsers(usersRes.data);
      setWeddings(weddingsRes.data);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateRole = async () => {
    try {
      const token = localStorage.getItem("token");
      const params = new URLSearchParams({ role: roleData.role });
      if (roleData.wedding_id) params.append("wedding_id", roleData.wedding_id);
      
      const response = await axios.put(
        `${API}/users/${selectedUser.user_id}/role?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setUsers(users.map(u => 
        u.user_id === selectedUser.user_id ? response.data : u
      ));
      setEditModalOpen(false);
      toast.success("User role updated");
    } catch (error) {
      toast.error("Failed to update role");
    }
  };

  const openEditModal = (user) => {
    setSelectedUser(user);
    setRoleData({ role: user.role, wedding_id: user.wedding_id || "" });
    setEditModalOpen(true);
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getRoleBadgeClass = (role) => {
    switch (role) {
      case "super_admin": return "bg-wedding-gold/20 text-wedding-gold border-wedding-gold/30";
      case "couple_admin": return "badge-active";
      default: return "bg-white/5 text-wedding-ivory/60 border-white/10";
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-users">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-3xl text-wedding-ivory">Users</h1>
          <p className="text-wedding-ivory/60 mt-1">Manage user roles and access</p>
        </div>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-wedding-ivory/40" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search users..."
            className="pl-10 bg-wedding-charcoal border-white/10 w-64"
          />
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Total Users", value: users.length, icon: Users },
          { label: "Admins", value: users.filter(u => u.role === "super_admin").length, icon: Shield },
          { label: "Couples", value: users.filter(u => u.role === "couple_admin").length, icon: Heart },
        ].map((stat) => (
          <div key={stat.label} className="stat-card">
            <stat.icon className="w-5 h-5 text-wedding-gold mb-2" />
            <div className="stat-value">{stat.value}</div>
            <div className="stat-label">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* Users Table */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-12 h-12 border-2 border-wedding-gold border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredUsers.length === 0 ? (
        <div className="text-center py-12">
          <Users className="w-16 h-16 text-wedding-gold/30 mx-auto mb-4" />
          <p className="text-wedding-ivory/60">No users found</p>
        </div>
      ) : (
        <div className="card-hover overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Assigned Wedding</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.user_id}>
                    <td>
                      <div className="flex items-center gap-3">
                        {user.picture ? (
                          <img src={user.picture} alt={user.name} className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-wedding-gold/20 flex items-center justify-center">
                            <span className="text-wedding-gold font-bold">{user.name?.[0]}</span>
                          </div>
                        )}
                        <span className="text-wedding-ivory">{user.name}</span>
                      </div>
                    </td>
                    <td className="text-wedding-ivory/70">{user.email}</td>
                    <td>
                      <span className={`badge ${getRoleBadgeClass(user.role)}`}>
                        {user.role?.replace("_", " ")}
                      </span>
                    </td>
                    <td className="text-wedding-ivory/70 text-sm">
                      {user.wedding_id ? (
                        weddings.find(w => w.wedding_id === user.wedding_id)
                          ? `${weddings.find(w => w.wedding_id === user.wedding_id).couple_name_1} & ${weddings.find(w => w.wedding_id === user.wedding_id).couple_name_2}`
                          : user.wedding_id
                      ) : "—"}
                    </td>
                    <td>
                      <Button
                        size="sm"
                        onClick={() => openEditModal(user)}
                        className="bg-white/5 text-wedding-ivory/60 hover:bg-white/10 border border-white/10"
                        data-testid={`edit-user-${user.user_id}`}
                      >
                        <Shield className="w-4 h-4 mr-1" />
                        Edit Role
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      <Dialog open={editModalOpen} onOpenChange={setEditModalOpen}>
        <DialogContent className="bg-wedding-charcoal border-white/10">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl text-wedding-ivory">Edit User Role</DialogTitle>
          </DialogHeader>
          {selectedUser && (
            <div className="space-y-6 mt-4">
              <div className="flex items-center gap-4 p-4 bg-wedding-subtle rounded-sm">
                {selectedUser.picture ? (
                  <img src={selectedUser.picture} alt={selectedUser.name} className="w-12 h-12 rounded-full" />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-wedding-gold/20 flex items-center justify-center">
                    <span className="text-wedding-gold font-bold text-xl">{selectedUser.name?.[0]}</span>
                  </div>
                )}
                <div>
                  <p className="text-wedding-ivory font-medium">{selectedUser.name}</p>
                  <p className="text-wedding-ivory/50 text-sm">{selectedUser.email}</p>
                </div>
              </div>

              <div>
                <label className="form-label">Role</label>
                <Select value={roleData.role} onValueChange={(v) => setRoleData({ ...roleData, role: v })}>
                  <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-wedding-charcoal border-white/10">
                    <SelectItem value="guest">Guest</SelectItem>
                    <SelectItem value="couple_admin">Couple Admin</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {roleData.role === "couple_admin" && (
                <div>
                  <label className="form-label">Assign to Wedding</label>
                  <Select value={roleData.wedding_id} onValueChange={(v) => setRoleData({ ...roleData, wedding_id: v })}>
                    <SelectTrigger className="w-full bg-wedding-subtle border-white/10 mt-2">
                      <SelectValue placeholder="Select wedding" />
                    </SelectTrigger>
                    <SelectContent className="bg-wedding-charcoal border-white/10">
                      <SelectItem value="">None</SelectItem>
                      {weddings.map(wedding => (
                        <SelectItem key={wedding.wedding_id} value={wedding.wedding_id}>
                          {wedding.couple_name_1} & {wedding.couple_name_2}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="flex justify-end gap-3">
                <Button onClick={() => setEditModalOpen(false)} className="btn-secondary">
                  Cancel
                </Button>
                <Button onClick={handleUpdateRole} className="btn-primary">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
